import { FunctionComponent, memo, useContext } from "react";
import { IProps } from "./edit-tag-group.types";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import Modal from "@edgetier/modal";
import { ITagGroup, Route } from "@edgetier/watchtower-types";
import { axios } from "utilities/axios";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import TagGroupForm from "components-for/tags/tag-group-form";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import { SpinnerUntil } from "@edgetier/components";
import { ITagGroupFormValues } from "components-for/tags/tag-group-form/tag-group-form.types";
import TagsListContext from "pages/tag-phrase-triggers/tags-list/tags-list-context/tags-list-context";
import "./edit-tag-group.scss";

/**
 * A button that triggers a modal containing a form used to edit the tag group.
 * @param props.tagGroupId The ID of the tag group being edited.
 */
const EditTagGroup: FunctionComponent<IProps> = ({ tagGroupId }) => {
    const queryClient = useQueryClient();
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { tagGroupType } = useContext(TagsListContext);

    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    const { data: tagGroup, isLoading } = useTagGroups(
        { tagGroupType },
        {
            select: (groups) => groups.find((group) => group.tagGroupId === tagGroupId),
        }
    );

    /**
     * Do a PATCH request to the backend to update the tag group.
     * @param values The new values of the tag group.
     */
    const onSubmit = async (values: ITagGroupFormValues) => {
        try {
            const url = Route.TagGroup.replace(":tagGroupId", tagGroupId);
            await axios.patch(url, values);

            queryClient.setQueriesData<ITagGroup[]>(Route.TagGroups, (groups) => {
                return (groups ?? []).map((group) =>
                    group.tagGroupId === values.tagGroupId ? { ...group, ...(values as ITagGroup) } : group
                );
            });

            toast.success(`"${values.tagGroup}" has been edited successfully`);
            closeModal();
        } catch (serverError) {
            toast.error(getServerError(serverError));
        }
    };

    return (
        <>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faEdit}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Edit
            </EllipsisMenuItem>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Edit Tag Group</h2>
                </ModalHeader>
                <ModalContent>
                    <SpinnerUntil<ITagGroup> data={tagGroup} isReady={!isLoading && typeof tagGroup !== "undefined"}>
                        {(data) => <TagGroupForm initialValues={data} onSubmit={onSubmit} />}
                    </SpinnerUntil>
                </ModalContent>
            </Modal>
        </>
    );
};

export default memo(EditTagGroup);
